import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LuPlus, LuTrash2 } from "react-icons/lu";

import { pokerApi } from "../../api/pokerApi";
import { showToast } from "../../utils/toast/Toast";
import { useUserContext } from "../../context/userContext";
import Button from "../ui/Button";
import Card from "../ui/Card";

// Matches src/components/user/LoginPage.jsx's input styling exactly -- the
// one existing text-input convention in this app, no dedicated ui/Input.jsx
// to import instead.
const inputClass =
  "w-full px-3 py-2.5 text-sm rounded-xl bg-surface border border-border-soft text-text-primary placeholder:text-text-faint outline-none transition-colors duration-150 focus:border-brand-cyan/50";
const labelClass = "block text-[10px] font-semibold tracking-[0.2em] uppercase mb-1.5 text-text-muted";
const errorClass = "mt-1 text-[11px] text-brand-rose";

function errorMessage(error, fallback) {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data.error === "string") return data.error;
  const first = Object.values(data).flat()[0];
  return typeof first === "string" ? first : fallback;
}

function toLocalDatetimeInputValue(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
}

const DEFAULT_SCHEDULED_START = toLocalDatetimeInputValue(new Date(Date.now() + 60 * 60 * 1000));

// UI works in minutes throughout (blind levels/breaks are always described
// that way in real tournament terms -- 5-minute levels, not "300 seconds").
// The backend still wants duration_seconds; converted only at submit time
// (see handleSubmit). Turn timer (single-table mode) deliberately stays in
// seconds -- it's a per-action clock in the 15-120s range, not a multi-minute
// blind level, and reads naturally as seconds the same way it already does
// everywhere else in this app.
const DEFAULT_BLIND_LEVELS = [
  { sb: 10, bb: 20, ante: 0, duration_minutes: 5 },
  { sb: 20, bb: 40, ante: 0, duration_minutes: 5 },
];

export default function CreateTournamentPage() {
  const navigate = useNavigate();
  const { user } = useUserContext();
  // /tournaments/edit/:type/:id (type "single"|"mtt") vs /tournaments/create --
  // same component, same field components, per the edit feature's explicit
  // "reuse the creation form" instruction rather than a parallel edit form.
  const { type: editType, id: editId } = useParams();
  const isEdit = Boolean(editType && editId);
  const [mode, setMode] = useState(editType === "mtt" ? "mtt" : "single"); // "single" | "mtt"
  const [loadingExisting, setLoadingExisting] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Single-table (ScheduledPokerMatch) fields -- writable fields per
  // ScheduledPokerMatchSerializer, defaults matching the model's own.
  const [title, setTitle] = useState("");
  const [scheduledStart, setScheduledStart] = useState(DEFAULT_SCHEDULED_START);
  const [maxPlayers, setMaxPlayers] = useState(6);
  const [startingChips, setStartingChips] = useState(1000);
  const [smallBlind, setSmallBlind] = useState(10);
  const [bigBlind, setBigBlind] = useState(20);
  const [turnTimerSeconds, setTurnTimerSeconds] = useState(45);
  // Optional blind escalation -- "" means unset (flat blinds for the whole
  // session, unchanged default behavior), not 0. Editable pre-start same as
  // the other Single Table fields (unlike scheduled_start/turn_timer_seconds).
  const [levelDurationMinutes, setLevelDurationMinutes] = useState("");

  // MTT (Tournament, via tournament_creation_service.create_tournament) fields.
  const [mttName, setMttName] = useState("");
  const [startingStack, setStartingStack] = useState(1500);
  const [maxTables, setMaxTables] = useState(2);
  const [blindLevels, setBlindLevels] = useState(DEFAULT_BLIND_LEVELS);
  const [breaks, setBreaks] = useState([]);

  // The one genuinely shared concept: a display name. Different backend
  // field name per mode (title vs name), same UI slot -- not duplicated
  // layout, per the shared-fields instruction.
  const name = mode === "single" ? title : mttName;
  const setName = mode === "single" ? setTitle : setMttName;

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        if (editType === "single") {
          const data = await pokerApi.getTournament(editId);
          if (cancelled) return;
          if (!data.is_creator) {
            showToast("error", "Only the tournament creator can edit it.");
            navigate("/tournaments?tab=scheduled");
            return;
          }
          setTitle(data.title);
          setMaxPlayers(data.max_players);
          setStartingChips(data.starting_chips);
          setSmallBlind(data.small_blind);
          setBigBlind(data.big_blind);
          setLevelDurationMinutes(data.level_duration_minutes ?? "");
          // scheduled_start/turn_timer_seconds aren't editable -- left at
          // their create-mode defaults, which is moot since SingleTableFields
          // hides both inputs in edit mode.
        } else {
          const data = await pokerApi.getMttTournament(editId);
          if (cancelled) return;
          if (data.created_by !== user?.id) {
            showToast("error", "Only the tournament creator can edit it.");
            navigate("/tournaments?tab=mtt");
            return;
          }
          setMttName(data.name);
          setStartingStack(data.starting_stack);
          setMaxTables(data.max_tables);
          // Backend field is duration_seconds; UI works in minutes throughout
          // (see the module note above DEFAULT_BLIND_LEVELS) -- converted here,
          // the mirror image of handleSubmit's minutes->seconds conversion.
          setBlindLevels(
            data.blind_schedule.map((lvl) => ({
              sb: lvl.sb,
              bb: lvl.bb,
              ante: lvl.ante,
              duration_minutes: lvl.duration_seconds / 60,
            }))
          );
          setBreaks(
            (data.break_schedule || []).map((b) => ({
              after_level: b.after_level,
              duration_minutes: b.duration_seconds / 60,
            }))
          );
        }
      } catch (error) {
        if (cancelled) return;
        showToast("error", errorMessage(error, "Could not load tournament."));
        navigate("/tournaments");
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editType, editId]);

  const updateBlindLevel = (index, field, value) => {
    setBlindLevels((levels) => levels.map((lvl, i) => (i === index ? { ...lvl, [field]: value } : lvl)));
  };
  const addBlindLevel = () => {
    setBlindLevels((levels) => {
      const last = levels[levels.length - 1];
      return [
        ...levels,
        last
          ? { sb: last.sb * 2, bb: last.bb * 2, ante: last.ante, duration_minutes: last.duration_minutes }
          : { sb: 10, bb: 20, ante: 0, duration_minutes: 5 },
      ];
    });
  };
  const removeBlindLevel = (index) => {
    setBlindLevels((levels) => levels.filter((_, i) => i !== index));
    // A removed level shifts every later break's after_level target -- drop
    // any break that pointed at or past the removed level rather than leave
    // it silently pointing at the wrong one.
    setBreaks((rows) => rows.filter((b) => Number(b.after_level) < index + 1));
  };

  const updateBreak = (index, field, value) => {
    setBreaks((rows) => rows.map((b, i) => (i === index ? { ...b, [field]: value } : b)));
  };
  const addBreak = () => {
    setBreaks((rows) => [...rows, { after_level: 1, duration_minutes: 1 }]);
  };
  const removeBreak = (index) => {
    setBreaks((rows) => rows.filter((_, i) => i !== index));
  };

  const validateSingle = () => {
    const next = {};
    if (title.trim().length < 3) next.title = "Title must be at least 3 characters.";
    // scheduled_start isn't editable (see SingleTableFields' editMode prop --
    // the field isn't even rendered in edit mode), so it's only validated at
    // creation time.
    if (!isEdit) {
      if (!scheduledStart) next.scheduledStart = "Scheduled start is required.";
      else if (new Date(scheduledStart).getTime() <= Date.now()) {
        next.scheduledStart = "Scheduled start must be in the future.";
      }
    }
    if (maxPlayers < 2 || maxPlayers > 9) next.maxPlayers = "Must be between 2 and 9.";
    if (startingChips < 500 || startingChips > 10000) next.startingChips = "Must be between 500 and 10,000.";
    if (smallBlind < 5) next.smallBlind = "Must be at least 5.";
    if (bigBlind < smallBlind * 2) next.bigBlind = "Must be at least double the small blind.";
    else if (bigBlind > Math.floor(startingChips / 5)) next.bigBlind = "Too high for the starting stack.";
    // turn_timer_seconds isn't editable either -- same reasoning as scheduled_start.
    if (!isEdit && (turnTimerSeconds < 15 || turnTimerSeconds > 120)) {
      next.turnTimerSeconds = "Must be between 15 and 120 seconds.";
    }
    if (levelDurationMinutes !== "" && Number(levelDurationMinutes) < 1) {
      next.levelDurationMinutes = "Must be at least 1 minute, or left blank for flat blinds.";
    }
    return next;
  };

  const validateMtt = () => {
    const next = {};
    if (mttName.trim().length < 3) next.name = "Name must be at least 3 characters.";
    if (Number(startingStack) < 1) next.startingStack = "Must be at least 1.";
    if (maxTables < 1 || maxTables > 6) next.maxTables = "Must be between 1 and 6.";
    if (blindLevels.length === 0) next.blindLevels = "At least one blind level is required.";
    blindLevels.forEach((lvl, i) => {
      if (Number(lvl.bb) < Number(lvl.sb)) next[`blindBb${i}`] = "BB must be ≥ SB.";
      // >=1 minute, not the backend's raw 5-second floor -- a sub-minute
      // blind level isn't a real input this form should accept; the backend
      // still enforces its own 5s floor as the actual source of truth.
      if (Number(lvl.duration_minutes) < 1) next[`blindDuration${i}`] = "Must be at least 1 minute.";
      if (Number(lvl.ante) < 0) next[`blindAnte${i}`] = "Cannot be negative.";
    });
    breaks.forEach((b, i) => {
      const afterLevel = Number(b.after_level);
      if (!(afterLevel >= 1 && afterLevel < blindLevels.length)) {
        next[`breakAfter${i}`] = `Must be between 1 and ${Math.max(1, blindLevels.length - 1)}.`;
      }
      if (Number(b.duration_minutes) < 0) next[`breakDuration${i}`] = "Cannot be negative.";
    });
    return next;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const fieldErrors = mode === "single" ? validateSingle() : validateMtt();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      showToast("error", "Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "single") {
        const payload = {
          title: title.trim(),
          max_players: Number(maxPlayers),
          starting_chips: Number(startingChips),
          small_blind: Number(smallBlind),
          big_blind: Number(bigBlind),
          level_duration_minutes: levelDurationMinutes === "" ? null : Number(levelDurationMinutes),
        };
        if (isEdit) {
          await pokerApi.editTournament(editId, payload);
        } else {
          await pokerApi.createTournament({
            ...payload,
            scheduled_start: new Date(scheduledStart).toISOString(),
            turn_timer_seconds: Number(turnTimerSeconds),
          });
        }
        showToast("success", isEdit ? "Tournament updated." : "Tournament created.");
        navigate("/tournaments?tab=scheduled");
      } else {
        const payload = {
          name: mttName.trim(),
          starting_stack: Number(startingStack),
          max_tables: Number(maxTables),
          // UI works in minutes throughout; the backend's actual field is
          // duration_seconds -- converted right here at the API boundary,
          // the one place it needs to happen.
          blind_schedule: blindLevels.map((lvl, i) => ({
            level: i + 1,
            sb: Number(lvl.sb),
            bb: Number(lvl.bb),
            ante: Number(lvl.ante),
            duration_seconds: Math.round(Number(lvl.duration_minutes) * 60),
          })),
          break_schedule: breaks.map((b) => ({
            after_level: Number(b.after_level),
            duration_seconds: Math.round(Number(b.duration_minutes) * 60),
          })),
        };
        if (isEdit) {
          await pokerApi.editMttTournament(editId, payload);
        } else {
          await pokerApi.createMttTournament(payload);
        }
        showToast("success", isEdit ? "Tournament updated." : "Tournament created.");
        navigate("/tournaments?tab=mtt");
      }
    } catch (error) {
      showToast("error", errorMessage(error, isEdit ? "Could not update tournament." : "Could not create tournament."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingExisting) {
    return (
      <div className="w-full px-1 pb-4 pt-1 sm:px-4 sm:pb-24 sm:pt-6">
        <div className="mx-auto flex max-w-2xl flex-col gap-4">
          <Card variant="outline" className="grid min-h-[220px] place-items-center p-4 text-sm text-text-secondary">
            Loading tournament...
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-1 pb-4 pt-1 sm:px-4 sm:pb-24 sm:pt-6">
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        <div>
          <div className="hidden text-[11px] uppercase tracking-[0.28em] text-text-muted sm:block">Poker</div>
          <h1 className="font-['Space_Grotesk',Inter,sans-serif] text-xl font-semibold tracking-wide text-text-primary sm:text-2xl">
            {isEdit ? "Edit Tournament" : "Create Tournament"}
          </h1>
        </div>

        {/* Mode toggle only applies to creation -- tournament type isn't
            editable, so edit mode shows a plain label instead. Same visual
            pattern as PokerTournamentsPage's Scheduled/MTT toggle, duplicated
            rather than shared -- that page's own toggle is explicitly not to
            be touched beyond matching its look. */}
        {isEdit ? (
          <div className="w-fit rounded-button border border-border-soft bg-slate-950/40 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-text-muted">
            Editing: {mode === "single" ? "Single Table" : "Multi-Table"}
          </div>
        ) : (
          <div className="grid w-fit grid-cols-2 rounded-button border border-border-soft bg-slate-950/40 p-1">
            <ModeButton active={mode === "single"} onClick={() => setMode("single")}>
              Single Table
            </ModeButton>
            <ModeButton active={mode === "mtt"} onClick={() => setMode("mtt")}>
              Multi-Table
            </ModeButton>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Card variant="glass" className="p-4 sm:p-5">
            <div>
              <label className={labelClass}>Tournament Name</label>
              <input
                type="text"
                className={inputClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Friday Night Showdown"
                maxLength={80}
              />
              {mode === "single" && errors.title ? <p className={errorClass}>{errors.title}</p> : null}
              {mode === "mtt" && errors.name ? <p className={errorClass}>{errors.name}</p> : null}
            </div>
          </Card>

          {mode === "single" ? (
            <SingleTableFields
              editMode={isEdit}
              scheduledStart={scheduledStart}
              setScheduledStart={setScheduledStart}
              maxPlayers={maxPlayers}
              setMaxPlayers={setMaxPlayers}
              startingChips={startingChips}
              setStartingChips={setStartingChips}
              smallBlind={smallBlind}
              setSmallBlind={setSmallBlind}
              bigBlind={bigBlind}
              setBigBlind={setBigBlind}
              turnTimerSeconds={turnTimerSeconds}
              setTurnTimerSeconds={setTurnTimerSeconds}
              levelDurationMinutes={levelDurationMinutes}
              setLevelDurationMinutes={setLevelDurationMinutes}
              errors={errors}
            />
          ) : (
            <MttFields
              startingStack={startingStack}
              setStartingStack={setStartingStack}
              maxTables={maxTables}
              setMaxTables={setMaxTables}
              blindLevels={blindLevels}
              updateBlindLevel={updateBlindLevel}
              addBlindLevel={addBlindLevel}
              removeBlindLevel={removeBlindLevel}
              breaks={breaks}
              updateBreak={updateBreak}
              addBreak={addBreak}
              removeBreak={removeBreak}
              errors={errors}
            />
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/tournaments?tab=${mode === "single" ? "scheduled" : "mtt"}`)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save Changes" : "Create Tournament"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModeButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 rounded-button px-4 text-xs font-semibold uppercase tracking-[0.12em] transition ${
        active ? "bg-brand-cyan/10 text-brand-cyan" : "text-text-muted hover:bg-surface hover:text-text-primary"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
      {error ? <p className={errorClass}>{error}</p> : null}
    </div>
  );
}

function SingleTableFields({
  editMode = false,
  scheduledStart,
  setScheduledStart,
  maxPlayers,
  setMaxPlayers,
  startingChips,
  setStartingChips,
  smallBlind,
  setSmallBlind,
  bigBlind,
  setBigBlind,
  turnTimerSeconds,
  setTurnTimerSeconds,
  levelDurationMinutes,
  setLevelDurationMinutes,
  errors,
}) {
  return (
    <Card variant="glass" className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-5">
      {/* scheduled_start and turn_timer_seconds aren't editable pre-start --
          hidden entirely in edit mode rather than shown disabled, since
          neither is part of the PATCH payload once here. */}
      {!editMode ? (
        <Field label="Scheduled Start" error={errors.scheduledStart}>
          <input
            type="datetime-local"
            className={inputClass}
            value={scheduledStart}
            onChange={(e) => setScheduledStart(e.target.value)}
          />
        </Field>
      ) : null}
      <Field label="Max Players (2-9)" error={errors.maxPlayers}>
        <input
          type="number"
          min={2}
          max={9}
          className={inputClass}
          value={maxPlayers}
          onChange={(e) => setMaxPlayers(Number(e.target.value))}
        />
      </Field>
      <Field label="Starting Chips (500-10,000)" error={errors.startingChips}>
        <input
          type="number"
          min={500}
          max={10000}
          step={100}
          className={inputClass}
          value={startingChips}
          onChange={(e) => setStartingChips(Number(e.target.value))}
        />
      </Field>
      {!editMode ? (
        <Field label="Turn Timer Seconds (15-120)" error={errors.turnTimerSeconds}>
          <input
            type="number"
            min={15}
            max={120}
            className={inputClass}
            value={turnTimerSeconds}
            onChange={(e) => setTurnTimerSeconds(Number(e.target.value))}
          />
        </Field>
      ) : null}
      <Field label="Small Blind" error={errors.smallBlind}>
        <input
          type="number"
          min={5}
          className={inputClass}
          value={smallBlind}
          onChange={(e) => setSmallBlind(Number(e.target.value))}
        />
      </Field>
      <Field label="Big Blind (≥ 2× small blind)" error={errors.bigBlind}>
        <input
          type="number"
          min={smallBlind * 2}
          className={inputClass}
          value={bigBlind}
          onChange={(e) => setBigBlind(Number(e.target.value))}
        />
      </Field>
      <Field label="Blind Increase Every (minutes, optional)" error={errors.levelDurationMinutes}>
        <input
          type="number"
          min={1}
          className={inputClass}
          value={levelDurationMinutes}
          onChange={(e) => setLevelDurationMinutes(e.target.value)}
          placeholder="Flat blinds"
        />
      </Field>
    </Card>
  );
}

function MttFields({
  startingStack,
  setStartingStack,
  maxTables,
  setMaxTables,
  blindLevels,
  updateBlindLevel,
  addBlindLevel,
  removeBlindLevel,
  breaks,
  updateBreak,
  addBreak,
  removeBreak,
  errors,
}) {
  return (
    <>
      <Card variant="glass" className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-5">
        <Field label="Starting Stack" error={errors.startingStack}>
          <input
            type="number"
            min={1}
            className={inputClass}
            value={startingStack}
            onChange={(e) => setStartingStack(e.target.value)}
          />
        </Field>
        <Field label="Max Tables (1-6)" error={errors.maxTables}>
          <input
            type="number"
            min={1}
            max={6}
            className={inputClass}
            value={maxTables}
            onChange={(e) => setMaxTables(Number(e.target.value))}
          />
        </Field>
      </Card>

      <Card variant="glass" className="p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between">
          <label className={labelClass}>Blind Schedule</label>
          <Button type="button" variant="outline" size="sm" onClick={addBlindLevel}>
            <LuPlus size={14} />
            Add Level
          </Button>
        </div>
        {errors.blindLevels ? <p className={errorClass}>{errors.blindLevels}</p> : null}
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-[2rem_1fr_1fr_1fr_1fr_2rem] gap-2 px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-faint">
            <span>Lvl</span>
            <span>SB</span>
            <span>BB</span>
            <span>Ante</span>
            <span>Min</span>
            <span />
          </div>
          {blindLevels.map((level, index) => (
            <div key={index} className="grid grid-cols-[2rem_1fr_1fr_1fr_1fr_2rem] items-center gap-2">
              <span className="text-center text-xs font-semibold text-text-muted">{index + 1}</span>
              <input
                type="number"
                min={0}
                className={inputClass}
                value={level.sb}
                onChange={(e) => updateBlindLevel(index, "sb", e.target.value)}
              />
              <input
                type="number"
                min={0}
                className={inputClass}
                value={level.bb}
                onChange={(e) => updateBlindLevel(index, "bb", e.target.value)}
              />
              <input
                type="number"
                min={0}
                className={inputClass}
                value={level.ante}
                onChange={(e) => updateBlindLevel(index, "ante", e.target.value)}
              />
              <input
                type="number"
                min={1}
                className={inputClass}
                value={level.duration_minutes}
                onChange={(e) => updateBlindLevel(index, "duration_minutes", e.target.value)}
              />
              <button
                type="button"
                onClick={() => removeBlindLevel(index)}
                disabled={blindLevels.length <= 1}
                className="grid h-9 w-9 place-items-center rounded-lg border border-border-soft text-text-muted transition hover:border-brand-rose/30 hover:text-brand-rose disabled:opacity-30"
                aria-label={`Remove level ${index + 1}`}
              >
                <LuTrash2 size={14} />
              </button>
              {errors[`blindBb${index}`] || errors[`blindDuration${index}`] || errors[`blindAnte${index}`] ? (
                <p className={`col-span-6 -mt-1 ${errorClass}`}>
                  {errors[`blindBb${index}`] || errors[`blindDuration${index}`] || errors[`blindAnte${index}`]}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </Card>

      <Card variant="glass" className="p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between">
          <label className={labelClass}>Breaks (optional)</label>
          <Button type="button" variant="outline" size="sm" onClick={addBreak} disabled={blindLevels.length < 2}>
            <LuPlus size={14} />
            Add Break
          </Button>
        </div>
        {breaks.length === 0 ? (
          <p className="text-xs text-text-faint">No breaks scheduled.</p>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-[1fr_1fr_2rem] gap-2 px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-faint">
              <span>After Level</span>
              <span>Min</span>
              <span />
            </div>
            {breaks.map((brk, index) => (
              <div key={index} className="grid grid-cols-[1fr_1fr_2rem] items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={Math.max(1, blindLevels.length - 1)}
                  className={inputClass}
                  value={brk.after_level}
                  onChange={(e) => updateBreak(index, "after_level", e.target.value)}
                />
                <input
                  type="number"
                  min={0}
                  className={inputClass}
                  value={brk.duration_minutes}
                  onChange={(e) => updateBreak(index, "duration_minutes", e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => removeBreak(index)}
                  className="grid h-9 w-9 place-items-center rounded-lg border border-border-soft text-text-muted transition hover:border-brand-rose/30 hover:text-brand-rose"
                  aria-label={`Remove break ${index + 1}`}
                >
                  <LuTrash2 size={14} />
                </button>
                {errors[`breakAfter${index}`] || errors[`breakDuration${index}`] ? (
                  <p className={`col-span-3 -mt-1 ${errorClass}`}>
                    {errors[`breakAfter${index}`] || errors[`breakDuration${index}`]}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
